import {Avatar, Button, Input, InputNumber, message, Popover, Select, Tooltip} from 'antd'
import {
    course_createCourseStudentPayment_Api,
    course_deleteCourseStudentPayment_Api,
    course_editCourseStudentFinance_Api,
    course_getCourseStudentFinanceDetail_Api,
    course_getCourseStudentFinances_Api,
    course_getFinanceSummary_Api,
    course_recalculateCourseStudentCredits_Api,
    course_recalculateCreditsForCourse_Api,
    course_sendFeeReminderEmail_Api,
    course_sendFeeReminderEmailBulk_Api,
} from 'apis'
import {getId} from 'apis/model/base'
import _ from 'lodash'
import useAsyncAction from 'modules/asyncCache/useAsyncAction'
import useTranslate from 'modules/local/useTranslate'
import React, {useCallback, useContext, useMemo, useState} from 'react'
import {FaUserGraduate} from 'react-icons/fa6'
import {
    PiArrowsClockwise,
    PiCheckCircleFill,
    PiClockCountdown,
    PiCoinsFill,
    PiEnvelopeSimpleFill,
    PiGearFill,
    PiInfoFill,
    PiPencilFill,
    PiReceiptFill,
    PiTrash,
    PiXCircle,
} from 'react-icons/pi'
import {FullSideModalLayout} from 'components/Modal/SideModalLayout'
import {FeeTypesEditor} from '../components/FeeTypesEditor'
import {course_fee_mode_options, course_fee_structure_type_options, getCourseFeeLabel} from '../../../AppLocals/Course'
import {showDeleteConfirmDialog} from 'views/AppLocals/factory/createConfirmDialog'
import {notifyOnError} from 'views/AppLocals/factory/createErrorEvent'
import {NotificationActionTypes, successNotify} from 'views/AppLocals/factory/createNotification'
import {bindQueryParams} from 'views/AppLocals/functions/routerHelper'
import usePaginationHelper, {createPagerKey} from 'views/AppLocals/hooks/usePaginationHelper'
import {NullAsDefault, renderIf} from 'views/Shared'
import LoadingBlock from 'views/Loading/LoadingBlock'
import classNames from 'classnames'
import {useLocation} from 'react-use'
import {LayoutContext} from '../../../../components/layouts/Default/LayoutContext'
import getAliasName from '../../../../helpers/getAliasName'
import copyToClipboard from 'copy-to-clipboard'
import {IoCopyOutline} from 'react-icons/io5'
import formatCurrency from '../../../Billing/components/formatCurrency'
import {formatSimplifyDate} from '../../../../helpers/dateUtils'

// payment_status - KHỚP CourseStudent::PAYMENT_STATUS_* (BE): -1 pending (chưa đóng, mặc định), 1
// partial (đóng 1 phần), 9 paid (đã đóng đủ). Field này suy HOÀN TOÀN tự động từ
// total_fee_amount/waived_amount/paid_amount (CourseStudentFeeService::applyPaymentStatus()) - FE
// không có input nào ghi trực tiếp field này.
const PAYMENT_STATUS = {PENDING: -1, PARTIAL: 1, PAID: 9}

const money = (value) => formatCurrency(value ?? 0)

// ─── Shared small pieces ──────────────────────────────────────────────────────

const PaymentStatusBadge = ({status, size = 'sm'}) => {
    const t = useTranslate()
    const lg = size === 'lg'
    const base = lg ? 'px-3 py-1 text-sm' : 'px-2 py-0.5 text-xs'
    if (status === PAYMENT_STATUS.PAID) {
        return (
            <span className={classNames('inline-flex items-center gap-1 rounded-full font-semibold bg-green-100 text-green-700', base)}>
                <PiCheckCircleFill size={lg ? 14 : 11}/>{t('fully paid')}
            </span>
        )
    }
    if (status === PAYMENT_STATUS.PARTIAL) {
        return (
            <span className={classNames('inline-flex items-center gap-1 rounded-full font-semibold bg-amber-100 text-amber-700', base)}>
                <PiClockCountdown size={lg ? 14 : 11}/>{t('partially paid')}
            </span>
        )
    }
    return (
        <span className={classNames('inline-flex items-center gap-1 rounded-full font-semibold bg-red-100 text-red-600', base)}>
            <PiXCircle size={lg ? 14 : 11}/>{t('unpaid')}
        </span>
    )
}

const StatCard = ({label, value, color, icon, loading}) => (
    <div className="flex-1 min-w-[150px] p-4 rounded-2xl border border-color-100 background flex flex-col gap-1">
        <div className="flex items-center gap-2">
            <div className={classNames('p-1.5 rounded-lg flex flex-center', color?.bg)}>
                {icon}
            </div>
            <span className="text-xs font-semibold text-color-300 uppercase tracking-wider">{label}</span>
        </div>
        {loading ? (
            <div className="h-6 w-24 rounded background-200 animate-pulse"/>
        ) : (
            <span className={classNames('font-bold text-xl tracking-wide', color?.text)}>{value}</span>
        )}
    </div>
)

// Khối 4 thẻ KPI (Tổng dự thu/Đã thu/Còn nợ/Miễn giảm) - LUÔN hiện thành 1 block cố định (kể cả lúc
// đang tải hay chưa có dữ liệu), không ẩn hẳn đi vì bạn yêu cầu phải thấy ngay khối này ở đây.
const FinanceSummaryBlock = ({summary, loading}) => {
    const t = useTranslate()
    const s = summary || {}
    return (
        <div className="flex flex-wrap gap-3">
            <StatCard
                loading={loading}
                label={t('total receivable')}
                value={money(s.total_fee_amount)}
                color={{bg: 'bg-indigo-50', text: 'text-indigo-600'}}
                icon={<PiCoinsFill size={16} className="text-indigo-500"/>}
            />
            <StatCard
                loading={loading}
                label={t('actually collected')}
                value={money(s.paid_amount)}
                color={{bg: 'bg-green-50', text: 'text-green-600'}}
                icon={<PiCheckCircleFill size={16} className="text-green-500"/>}
            />
            <StatCard
                loading={loading}
                label={t('outstanding balance')}
                value={money(s.remaining_amount)}
                color={{bg: 'bg-red-50', text: 'text-red-600'}}
                icon={<PiClockCountdown size={16} className="text-red-500"/>}
            />
            <StatCard
                loading={loading}
                label={t('waived / scholarship')}
                value={money(s.waived_amount)}
                color={{bg: 'bg-amber-50', text: 'text-amber-600'}}
                icon={<PiReceiptFill size={16} className="text-amber-500"/>}
            />
        </div>
    )
}

// Tóm tắt phí đã khai ở Cấu hình Khóa học (theo yêu cầu) - "có thu phí không, cố định thì bao
// nhiêu, biểu phí thì liệt kê chi tiết từng loại" NGAY tại tab này, không phải nhảy sang tab Cấu
// hình mới biết. Dòng đầu dùng getCourseFeeLabel() - ĐÚNG hàm đang dùng ở tab Tổng quan/card khóa
// học (KHÔNG viết lại công thức format tiền/nhãn free-donation-paid riêng ở đây) - CHỈ RIÊNG case
// paid+itemized override nhãn (getCourseFeeLabel trả thẳng fee_amount, vốn không còn ý nghĩa khi đã
// chuyển sang tính theo định mức, xem Course::FEE_STRUCTURE_TYPE_ITEMIZED).
const FeeConfigSummaryBlock = ({course_id, fee_mode, fee_amount, fee_currency_code, fee_structure_type}) => {
    const t = useTranslate()
    const isPaid = fee_mode === course_fee_mode_options.paid.value
    const isItemized = fee_structure_type === course_fee_structure_type_options.itemized.value

    const headerLabel = (isPaid && isItemized)
        ? t('itemized fee structure label')
        : getCourseFeeLabel({fee_mode, fee_amount, fee_currency_code}, t, {freeLabelKey: 'completely free'})

    return (
        <div className="p-4 border border-color-100 rounded-xl background flex flex-col gap-2">
            <div className="flex items-center gap-2 flex-wrap">
                <PiInfoFill size={15} className="text-indigo-500 flex-shrink-0"/>
                <span className="font-semibold text-sm text-color-000">{t('fee configuration summary title')}</span>
                <span className="ml-auto font-bold text-sm text-indigo-600">{headerLabel}</span>
            </div>
            {isPaid && isItemized && (
                <div className="flex flex-col gap-2">
                    <p className="text-xs text-color-400 italic tracking-wide">
                        {t('itemized fee summary hint')}
                    </p>
                    <FeeTypesEditor course_id={course_id} can_edit={false}/>
                </div>
            )}
        </div>
    )
}

// Filter trạng thái - gắn vào ĐÚNG khe AdvancedFilter của usePaginationHelper (panel mở ra khi bấm
// icon phễu cạnh ô tìm kiếm) - đúng convention "Faceted Expand Panel" đang dùng ở các màn khác, KHÔNG
// tự vẽ 1 hàng filter rời rạc phía trên như trước.
// Options dùng chung cho 3 filter "Có/Không..." (theo yêu cầu) - so total_fee_amount/waived_amount/
// paid_amount với 0 ở BE (CourseFinanceController@getCourseStudentFinances), FE chỉ gửi đúng
// '1'/'0' qua query_params has_waived/has_paid/has_remaining.
const YES_NO_FILTER_OPTIONS = (t) => [
    {value: '1', label: t('yes')},
    {value: '0', label: t('no')},
]

const FinanceFilterPanel = ({value, onChange}) => {
    const t = useTranslate()
    const setField = (field) => (v) => onChange({...(value ?? {}), [field]: v})

    return (
        <div className="flex flex-col gap-3">
            <div className="flex items-center gap-2 flex-wrap">
                <span className="text-sm text-color-300 flex-shrink-0">{t('filter by status')}:</span>
                <Select
                    allowClear
                    placeholder={t('all statuses')}
                    value={value?.payment_status}
                    onChange={setField('payment_status')}
                    className="w-full sm:w-44"
                    options={[
                        {value: String(PAYMENT_STATUS.PENDING), label: t('unpaid')},
                        {value: String(PAYMENT_STATUS.PARTIAL), label: t('partially paid')},
                        {value: String(PAYMENT_STATUS.PAID), label: t('fully paid')},
                    ]}
                />
            </div>
            <div className="flex items-center gap-2 flex-wrap">
                <span className="text-sm text-color-300 flex-shrink-0">{t('has waived filter label')}:</span>
                <Select
                    allowClear
                    placeholder={t('all')}
                    value={value?.has_waived}
                    onChange={setField('has_waived')}
                    className="w-full sm:w-32"
                    options={YES_NO_FILTER_OPTIONS(t)}
                />
            </div>
            <div className="flex items-center gap-2 flex-wrap">
                <span className="text-sm text-color-300 flex-shrink-0">{t('has paid filter label')}:</span>
                <Select
                    allowClear
                    placeholder={t('all')}
                    value={value?.has_paid}
                    onChange={setField('has_paid')}
                    className="w-full sm:w-32"
                    options={YES_NO_FILTER_OPTIONS(t)}
                />
            </div>
            <div className="flex items-center gap-2 flex-wrap">
                <span className="text-sm text-color-300 flex-shrink-0">{t('has remaining filter label')}:</span>
                <Select
                    allowClear
                    placeholder={t('all')}
                    value={value?.has_remaining}
                    onChange={setField('has_remaining')}
                    className="w-full sm:w-32"
                    options={YES_NO_FILTER_OPTIONS(t)}
                />
            </div>
        </div>
    )
}

// ─── Fee types (Bảng Quy Định Định Mức) ───────────────────────────────────────
// Nội dung list/thêm/sửa/xoá định mức dùng CHUNG với CourseSetForm.js (hiện khi
// fee_structure_type=itemized ngay trong form Cấu hình Khóa học) - xem
// views/Workspace/Course/components/FeeTypesEditor.js, ở đây chỉ bọc Drawer.

const FeeTypesPanel = ({course_id, can_edit, onCancel}) => {
    const t = useTranslate()

    return (
        <FullSideModalLayout
            right={true}
            title={
                <div className="flex items-center gap-2">
                    <PiGearFill size={16} className="text-indigo-500"/>
                    <span className="font-bold text-color-000 tracking-wide">{t('fee rate settings')}</span>
                </div>
            }
            onCancel={onCancel}>
            <div className="px-4 pb-6 flex flex-col">
                <p className="mb-3 text-xs text-color-300 tracking-wide">
                    {t('fee rate settings description')}
                </p>
                <FeeTypesEditor course_id={course_id} can_edit={can_edit}/>
            </div>
        </FullSideModalLayout>
    )
}

// ─── Student finance detail (Bảng Kê Chi Phí Học Viên) ────────────────────────

const BreakdownTable = ({breakdown}) => {
    const t = useTranslate()
    if (!breakdown) return null

    if (!breakdown.is_itemized) {
        return (
            <div className="flex items-center justify-between py-2">
                <span className="text-sm text-color-300">{t('total fee')}</span>
                <span className="font-bold text-color-000">{money(breakdown.gross_amount)}</span>
            </div>
        )
    }

    const gap = breakdown.billed_credits_not_in_current_subjects ?? 0

    return (
        <div className="flex flex-col divide-y divide-color-50">
            {(breakdown.subjects ?? []).map((s, i) => (
                <div key={s.subject_id ?? i} className="flex items-center justify-between py-1.5 text-sm">
                    <span className="text-color-100 truncate">{s.title ?? '—'} ({s.number_of_credits} TC)</span>
                    <span className="font-medium text-color-000 flex-shrink-0">{money(s.amount)}</span>
                </div>
            ))}
            {gap > 0 && (
                <div className="flex items-center justify-between py-1.5 text-sm italic">
                    <span className="text-color-300">{t('credits recorded previously')} ({gap} TC)</span>
                    <span className="font-medium text-color-300 flex-shrink-0">
                        {money(gap * (breakdown.credit_line?.unit_price ?? 0))}
                    </span>
                </div>
            )}
            {(breakdown.flat_fees ?? []).map((f) => (
                <div key={f.fee_type_id} className="flex items-center justify-between py-1.5 text-sm">
                    <span className="text-color-100">{f.name}</span>
                    <span className="font-medium text-color-000">{money(f.amount)}</span>
                </div>
            ))}
        </div>
    )
}

const PaymentHistoryList = ({payments = [], can_edit, onDeleted}) => {
    const t = useTranslate()
    const onError = notifyOnError(t)
    const {handleAsyncAction: deletePayment} = useAsyncAction({
        apiInfo: course_deleteCourseStudentPayment_Api,
        onError,
        onSuccess: onDeleted,
    })

    if (payments.length === 0) {
        return <div className="py-3 text-center text-xs text-color-400 italic">{t('no payment recorded yet')}</div>
    }

    return (
        <div className="flex flex-col gap-2">
            {payments.map((p) => (
                <div key={p.id} className="flex items-center justify-between gap-2 p-2 rounded-lg background-100">
                    <div className="flex flex-col min-w-0">
                        {/* Khoản thu = CREDIT (tiền vào) - tô xanh lá + dấu cộng, đồng bộ màu với "Đã
                            đóng" ở khối tổng kết phía trên. */}
                        <span className="font-semibold text-sm text-green-600">{`+${money(p.amount)}`}</span>
                        <span className="text-xs text-color-300">
                            {formatSimplifyDate(p.received_at, 'medium')}
                            {p.method ? ` · ${p.method}` : ''}
                            {p.note ? ` · ${p.note}` : ''}
                        </span>
                    </div>
                    {can_edit && (
                        <Button
                            size="small"
                            type="text"
                            danger
                            icon={<PiTrash size={13}/>}
                            onClick={() => showDeleteConfirmDialog({
                                translate: t,
                                title: 'delete payment record',
                                message: 'are you sure you want to delete this payment record',
                                onOk: () => deletePayment({}, {id: getId(p)}),
                            })}
                        />
                    )}
                </div>
            ))}
        </div>
    )
}

const AddPaymentForm = ({course_student_id, onAdded}) => {
    const t = useTranslate()
    const [amount, setAmount] = useState(0)
    const [method, setMethod] = useState('BANK')
    const [note, setNote] = useState('')

    const onError = notifyOnError(t)
    const {handleAsyncAction: createPayment, isLoading} = useAsyncAction({
        apiInfo: course_createCourseStudentPayment_Api,
        onError,
        onSuccess: () => {
            setAmount(0)
            setNote('')
            onAdded()
            successNotify(NotificationActionTypes.SUCCESS, t)
        },
    })

    return (
        <div className="p-3 border border-dashed border-color-200 rounded-xl flex flex-col gap-2">
            <span className="font-semibold text-xs text-color-300 uppercase tracking-wider">{t('record new payment')}</span>
            <div className="flex items-center gap-2">
                <InputNumber
                    min={0}
                    step={100000}
                    addonAfter="đ"
                    value={amount}
                    onChange={setAmount}
                    formatter={(v) => `${v}`.replace(/\B(?=(\d{3})+(?!\d))/g, '.')}
                    parser={(v) => v.replace(/\./g, '')}
                    placeholder={t('amount')}
                    className="finance-amount-input flex-1"
                />
                {/* w-40 (thay w-28 cũ) - "Chuyển khoản" trước đây bị cắt chữ "Chuyển k..." vì không đủ
                    chỗ, cùng font-size với option dài nhất để không lặp lại lỗi này. */}
                <Select value={method} onChange={setMethod} className="w-40 flex-shrink-0" options={[
                    {value: 'CASH', label: t('cash')},
                    {value: 'BANK', label: t('bank transfer')},
                    {value: 'OTHER', label: t('other')},
                ]}/>
            </div>
            <Input placeholder={t('note optional')} value={note} onChange={(e) => setNote(e.target.value)}/>
            <Button
                type="primary"
                size="small"
                loading={isLoading}
                disabled={!amount || amount <= 0}
                onClick={() => createPayment({amount, method, note}, {id: course_student_id})}>
                {t('record payment')}
            </Button>
        </div>
    )
}

const StudentFinanceDetail = ({item, onChanged}) => {
    const t = useTranslate()
    const course_student_id = getId(item)
    const [detail, setDetail] = useState(null)
    const [waivedAmount, setWaivedAmount] = useState(null)
    const [creditOverride, setCreditOverride] = useState(null)

    const onError = notifyOnError(t)

    const {handleAsyncAction: fetchDetail, isLoading} = useAsyncAction({
        apiInfo: course_getCourseStudentFinanceDetail_Api,
        onSuccess: (result, data) => setDetail(_.get(data, 'response.data', null)),
    })

    const reload = useCallback(() => fetchDetail({}, {id: course_student_id}), [fetchDetail, course_student_id])

    React.useEffect(() => {
        if (course_student_id) reload()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [course_student_id])

    const {handleAsyncAction: saveWaived, isLoading: savingWaived} = useAsyncAction({
        apiInfo: course_editCourseStudentFinance_Api,
        onError,
        onSuccess: () => {
            reload()
            if (onChanged) onChanged()
        },
    })

    const {handleAsyncAction: recalculate, isLoading: recalculating} = useAsyncAction({
        apiInfo: course_recalculateCourseStudentCredits_Api,
        onError,
        onSuccess: () => {
            setCreditOverride(null)
            reload()
            if (onChanged) onChanged()
            successNotify(NotificationActionTypes.SUCCESS, t)
        },
    })

    const {handleAsyncAction: sendReminder, isLoading: sending} = useAsyncAction({
        apiInfo: course_sendFeeReminderEmail_Api,
        onError,
        onSuccess: () => {
            reload()
            if (onChanged) onChanged()
            message.success(t('email sent'))
        },
    })

    // Nút riêng "Gửi invoice học phí" (theo yêu cầu) - CÙNG action/API với "Gửi email nhắc đóng học
    // phí" bên dưới (CourseFinanceController@sendFeeReminderEmail), chỉ khác values.type='invoice' ->
    // BE build email theo khung "Hoá đơn học phí" trung lập thay vì tự suy nhắc/biên nhận theo
    // payment_status - xem App\Mail\CourseStudentFeeStatementEmail::MODE_INVOICE. CHIA SẺ CHUNG bộ
    // đếm reminder_sent_count/cooldown với nút kia (không tách giới hạn riêng, tránh spam gộp 2 nút).
    const {handleAsyncAction: sendInvoice, isLoading: sendingInvoice} = useAsyncAction({
        apiInfo: course_sendFeeReminderEmail_Api,
        onError,
        onSuccess: () => {
            reload()
            if (onChanged) onChanged()
            message.success(t('email sent'))
        },
    })

    if (isLoading || detail === null) return <LoadingBlock/>

    const student = detail.student ?? {}
    const breakdown = detail.breakdown ?? {}
    const canSendReminder = detail.can_send_reminder
    const reminderCount = detail.reminder_sent_count ?? 0
    const reminderMax = detail.reminder_max_count ?? 2

    return (
        <div className="px-3 flex flex-col gap-4">
            {/* Student header */}
            <div className="p-3 border border-color-50 shadow-md rounded-xl flex items-center gap-3">
                <Avatar
                    size={40}
                    src={student?.avatar || student?.user?.avatar}
                    icon={<FaUserGraduate size={20} className="text-color-500"/>}
                    className="flex flex-center background-200"
                />
                <div className="flex flex-col gap-0.5 min-w-0">
                    <span className="font-bold text-color-000 truncate">{student?.fullname}</span>
                    {student?.student_idcode && (
                        <span
                            onClick={() => {
                                copyToClipboard(student.student_idcode)
                                message.info(t('copied'))
                            }}
                            className="font-semibold text-xs text-blue-500 hover:text-blue-700 tracking-wider cursor-pointer">
                            #{student.student_idcode}
                            <IoCopyOutline size={10} className="ml-1"/>
                        </span>
                    )}
                </div>
                <div className="ml-auto">
                    <PaymentStatusBadge status={detail.payment_status} size="lg"/>
                </div>
            </div>

            {/* Breakdown - phân cấp màu theo Ý NGHĨA số tiền (bạn yêu cầu rõ ràng hơn, debit/credit rõ
                hơn): tổng thô = phụ/nhạt (chỉ để tham khảo cách tính) -> miễn giảm = số bị TRỪ, tô hổ
                phách + dấu trừ -> "Số tiền phải đóng" = con số QUAN TRỌNG NHẤT, tách khối nền riêng +
                to nhất -> Đã đóng = CREDIT (tiền vào, xanh lá + dấu cộng) -> Còn nợ = DEBIT (tiền còn
                thiếu, đỏ + dấu trừ, hết nợ thì xanh lá). */}
            <div className="p-3 border border-color-50 rounded-xl">
                <span className="font-semibold text-sm text-color-000 tracking-wide">{t('fee statement details')}</span>
                <div className="mt-1 w-16 h-0.5 bg-gray-300 rounded-full mb-2"/>
                <BreakdownTable breakdown={breakdown}/>
                <div className="mt-2 pt-2 border-t border-color-100 flex flex-col gap-2">
                    <div className="flex items-center justify-between text-sm">
                        <span className="text-color-300">{t('gross total')}</span>
                        <span className="font-medium text-color-300">{money(breakdown.gross_amount)}</span>
                    </div>
                    <div className="flex items-center justify-between">
                        <span className="text-sm text-color-300">{t('waived / scholarship')}</span>
                        <div className="flex items-center gap-2">
                            <InputNumber
                                min={0}
                                step={50000}
                                addonAfter="đ"
                                value={waivedAmount ?? breakdown.waived_amount}
                                onChange={setWaivedAmount}
                                formatter={(v) => `${v}`.replace(/\B(?=(\d{3})+(?!\d))/g, '.')}
                                parser={(v) => v.replace(/\./g, '')}
                                className="finance-amount-input finance-amount-input-deduct w-36"
                            />
                            {waivedAmount !== null && Number(waivedAmount) !== Number(breakdown.waived_amount) && (
                                <Button size="small" type="primary" loading={savingWaived}
                                        onClick={() => saveWaived({waived_amount: waivedAmount}, {id: course_student_id})}>
                                    {t('save')}
                                </Button>
                            )}
                        </div>
                    </div>

                    {/* Con số quan trọng nhất của cả bảng kê - tách hẳn 1 khối nền riêng (không còn
                        cùng cấp bậc thị giác với các dòng phụ trên/dưới), to nhất + màu chủ đạo (indigo). */}
                    <div className="-mx-3 px-3 py-2 bg-indigo-50 flex items-center justify-between">
                        <span className="font-bold text-sm text-indigo-900">{t('amount payable')}</span>
                        <span className="font-black text-xl text-indigo-700 tracking-wide">{money(breakdown.net_amount)}</span>
                    </div>

                    <div className="flex items-center justify-between text-sm">
                        <span className="text-color-300">{t('amount paid')}</span>
                        <span className="font-bold text-green-600">{`+${money(breakdown.paid_amount)}`}</span>
                    </div>
                    <div className="flex items-center justify-between font-bold">
                        <span className={breakdown.remaining_amount > 0 ? 'text-red-600' : 'text-green-600'}>
                            {breakdown.remaining_amount > 0 ? t('remaining balance') : t('fully paid')}
                        </span>
                        <span className={breakdown.remaining_amount > 0 ? 'text-red-600' : 'text-green-600'}>
                            {breakdown.remaining_amount > 0 ? `-${money(breakdown.remaining_amount)}` : money(breakdown.remaining_amount)}
                        </span>
                    </div>
                </div>
            </div>

            {/* Recalculate credits (rà soát thủ công) */}
            {breakdown.is_itemized && (
                <div className="p-3 border border-color-50 rounded-xl flex flex-col gap-2">
                    <div className="flex items-center justify-between">
                        <span className="font-semibold text-sm text-color-000">{t('credit reconciliation')}</span>
                        <Button size="small" icon={<PiArrowsClockwise size={13}/>} loading={recalculating}
                                onClick={() => recalculate({}, {id: course_student_id})}>
                            {t('recalculate')}
                        </Button>
                    </div>
                    <div className="flex items-center gap-4 text-sm">
                        <span className="text-color-300">{t('credits billed')}: <b className="text-color-000">{breakdown.credit_line?.credit_count_billed}</b></span>
                        <span className="text-color-300">{t('credits in current classes')}: <b className="text-color-000">{breakdown.credit_line?.credit_count_current}</b></span>
                    </div>
                    <div className="flex items-center gap-2">
                        <InputNumber
                            min={0}
                            size="small"
                            placeholder={t('override billed credits')}
                            value={creditOverride}
                            onChange={setCreditOverride}
                            className="w-40"
                        />
                        {creditOverride !== null && (
                            <Button size="small" type="primary" loading={recalculating}
                                    onClick={() => recalculate({credit_count_billed: creditOverride}, {id: course_student_id})}>
                                {t('apply override')}
                            </Button>
                        )}
                    </div>
                </div>
            )}

            {/* Payments */}
            <div className="p-3 border border-color-50 rounded-xl flex flex-col gap-3">
                <span className="font-semibold text-sm text-color-000 tracking-wide">{t('payment history')}</span>
                <PaymentHistoryList payments={detail.payments ?? []} can_edit={true} onDeleted={() => {
                    reload()
                    if (onChanged) onChanged()
                }}/>
                <AddPaymentForm course_student_id={course_student_id} onAdded={() => {
                    reload()
                    if (onChanged) onChanged()
                }}/>
            </div>

            {/* Send email - 2 nút CHIA SẺ chung 1 giới hạn/cooldown (canSendReminder) */}
            <div className="flex flex-col sm:flex-row gap-2">
                <Button
                    className="flex-1"
                    type="dashed"
                    icon={<PiEnvelopeSimpleFill size={14}/>}
                    loading={sending}
                    disabled={!canSendReminder}
                    onClick={() => sendReminder({}, {id: course_student_id})}>
                    {detail.payment_status === PAYMENT_STATUS.PAID ? t('send payment receipt email') : t('send fee reminder email')}
                </Button>
                <Button
                    className="flex-1"
                    type="dashed"
                    icon={<PiReceiptFill size={14}/>}
                    loading={sendingInvoice}
                    disabled={!canSendReminder}
                    onClick={() => sendInvoice({type: 'invoice'}, {id: course_student_id})}>
                    {t('send tuition invoice email')}
                </Button>
            </div>
            <span className="-mt-2 text-xs text-color-400 text-center">
                {canSendReminder
                    ? t('reminder email sent count', {count: reminderCount, max: reminderMax})
                    : t('reminder email limit reached')}
            </span>
            <div className="h-6"/>
        </div>
    )
}

// ─── Main list row ────────────────────────────────────────────────────────────

// Header/từng dòng là các div GRID ĐỘC LẬP với nhau (không phải 1 grid/table chia sẻ chung) - dùng
// bất kỳ đơn vị co giãn nào (fr/%, kể cả minmax(...,max-content)) đều khiến MỖI dòng tự tính độ rộng
// cột theo NỘI DUNG RIÊNG của dòng đó (vd tên dài/ngắn khác nhau) -> các dòng lệch cột với nhau và
// với header (đã thử 2fr rồi minmax(...,max-content), cả 2 đều vẫn lệch theo cách khác nhau). CHỐT:
// toàn bộ cột dùng PX CỐ ĐỊNH - mọi dòng/header luôn tính ra ĐÚNG 1 độ rộng duy nhất, không thể lệch.
// Tên dài thì truncate (đã có class truncate ở StudentFinanceRow), không co giãn cột theo tên nữa.
// Kết hợp overflow-x-auto (renderWrap bên dưới) để cuộn ngang khi màn hẹp hơn tổng độ rộng cột.
const FINANCE_ROW_GRID = '260px 110px 70px 130px 120px 120px 120px 110px 130px'

// Sửa nhanh "Miễn giảm" ngay tại dòng danh sách (theo yêu cầu) - Popover + InputNumber, KHÔNG cần
// mở hẳn Drawer chi tiết chỉ để đổi 1 số. Cùng API course_editCourseStudentFinance_Api dùng ở
// StudentFinanceDetail (1 nguồn duy nhất), chỉ khác nơi gọi.
const WaivedAmountCell = ({item, onChanged}) => {
    const t = useTranslate()
    const [open, setOpen] = useState(false)
    const [value, setValue] = useState(item.waived_amount)

    const onError = notifyOnError(t)
    const {handleAsyncAction: saveWaived, isLoading} = useAsyncAction({
        apiInfo: course_editCourseStudentFinance_Api,
        onError,
        onSuccess: () => {
            setOpen(false)
            if (onChanged) onChanged()
        },
    })

    return (
        <Popover
            trigger="click"
            open={open}
            onOpenChange={(v) => {
                setOpen(v)
                if (v) setValue(item.waived_amount)
            }}
            content={
                <div className="flex items-center gap-2">
                    <InputNumber
                        min={0}
                        step={50000}
                        autoFocus
                        value={value}
                        onChange={setValue}
                        formatter={(v) => `${v}`.replace(/\B(?=(\d{3})+(?!\d))/g, '.')}
                        parser={(v) => v.replace(/\./g, '')}
                        className="w-32"
                    />
                    <Button
                        size="small"
                        type="primary"
                        loading={isLoading}
                        onClick={() => saveWaived({waived_amount: value}, {id: getId(item)})}>
                        {t('save')}
                    </Button>
                </div>
            }>
            <button className="inline-flex items-center gap-1 text-color-400 hover:text-indigo-600 transition-colors">
                <span>{item.waived_amount > 0 ? `-${money(item.waived_amount)}` : '—'}</span>
                <PiPencilFill size={11}/>
            </button>
        </Popover>
    )
}

// Tính lại tín chỉ/tiền NGAY trên dòng đó (theo yêu cầu) - cùng API
// course_recalculateCourseStudentCredits_Api dùng ở màn rà soát chi tiết, không ghi đè billed (chỉ
// đồng bộ theo lớp hiện tại, giống nút "Rà soát toàn bộ" ở header nhưng cho riêng 1 học viên).
const RecalculateRowButton = ({item, onChanged}) => {
    const t = useTranslate()
    const onError = notifyOnError(t)
    const {handleAsyncAction: recalculate, isLoading} = useAsyncAction({
        apiInfo: course_recalculateCourseStudentCredits_Api,
        onError,
        onSuccess: () => {
            message.success(t('recalculated'))
            if (onChanged) onChanged()
        },
    })

    return (
        <Tooltip title={t('recalculate credits')}>
            <button
                onClick={() => recalculate({}, {id: getId(item)})}
                disabled={isLoading}
                className="inline-flex items-center text-color-400 hover:text-indigo-600 transition-colors disabled:opacity-50">
                <PiArrowsClockwise size={14} className={isLoading ? 'animate-spin' : ''}/>
            </button>
        </Tooltip>
    )
}

// Cột "Môn học" (theo yêu cầu) - hiện số môn dạng badge, bấm vào (Popover, cùng cách WaivedAmountCell
// đang dùng) mới xổ ra danh sách từng môn kèm số tín chỉ - registered_subjects lấy từ
// course_students.subject_ids, xem CourseStudentFinanceTransformer::buildRegisteredSubjects() (BE).
const SubjectsCell = ({item}) => {
    const t = useTranslate()
    const subjects = item?.registered_subjects ?? []

    if (subjects.length === 0) {
        return <span className="text-color-400">—</span>
    }

    return (
        <Popover
            trigger="click"
            content={
                <div className="flex flex-col gap-1 max-w-xs">
                    {subjects.map((s) => (
                        <div key={s.id} className="flex items-center justify-between gap-3 text-sm">
                            <span className="text-color-100 truncate">{s.title}</span>
                            <span className="font-semibold text-color-000 flex-shrink-0">{s.number_of_credits} TC</span>
                        </div>
                    ))}
                </div>
            }>
            <button className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-indigo-50 text-indigo-600 text-xs font-semibold hover:bg-indigo-100 transition-colors">
                {t('subjects count', {count: subjects.length})}
            </button>
        </Popover>
    )
}

const StudentFinanceRow = ({item, onView, onChanged}) => {
    const t = useTranslate()
    const {isLg} = useContext(LayoutContext)
    const student = item?.student ?? {}
    const remaining = item?.remaining_amount ?? 0

    const viewBtn = (
        <button
            onClick={() => onView(item)}
            className="inline-flex items-center gap-1 text-xs text-indigo-500 hover:text-indigo-700 transition-colors">
            <PiReceiptFill size={13}/>
            <span className="hidden md:inline">{t('view detail')}</span>
        </button>
    )

    if (!isLg) {
        return (
            <div className="border-b last:border-b-0 px-3 py-2.5 flex items-center justify-between gap-3">
                <div className="flex flex-col gap-1 min-w-0 flex-1">
                    <span className="font-semibold text-sm text-color-000 truncate">{student.fullname}</span>
                    <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-xs text-color-400">{item.credit_count_billed} TC</span>
                        <span className="text-xs font-semibold text-color-000">{money(item.total_fee_amount)}</span>
                        <PaymentStatusBadge status={item.payment_status}/>
                    </div>
                </div>
                {viewBtn}
            </div>
        )
    }

    return (
        <div
            className="border-b last:border-b-0 hover:background-50 grid items-center px-3 py-2 gap-3 text-sm"
            style={{gridTemplateColumns: FINANCE_ROW_GRID, width: 'max-content'}}>
            <div className="flex items-center gap-3 min-w-0">
                <Avatar
                    size={32}
                    src={student?.avatar || student?.user?.avatar}
                    icon={<FaUserGraduate size={14} className="text-color-500"/>}
                    className="flex flex-center background-200"
                />
                <div className="flex flex-col min-w-0">
                    <span className="font-bold text-color-000 truncate">
                        {renderIf(student?.patron_saint_name, <span className="mr-1 font-medium text-sm text-slate-600">{getAliasName(student, 'patron_saint')}</span>)}
                        {student.fullname}
                    </span>
                    {student?.student_idcode && <span className="font-semibold text-xs text-blue-500 tracking-wider">#{student.student_idcode}</span>}
                </div>
            </div>
            <div><SubjectsCell item={item}/></div>
            <div className="text-center font-semibold text-color-000">{item.credit_count_billed} TC</div>
            <div className="font-semibold text-color-000">{money(item.total_fee_amount)}</div>
            <div><WaivedAmountCell item={item} onChanged={onChanged}/></div>
            <div className="text-green-600 font-medium">{money(item.paid_amount)}</div>
            <div className={remaining > 0 ? 'text-red-600 font-semibold' : 'text-color-400'}>{money(remaining)}</div>
            <div><PaymentStatusBadge status={item.payment_status}/></div>
            <div className="flex items-center justify-end gap-3">
                <RecalculateRowButton item={item} onChanged={onChanged}/>
                {viewBtn}
            </div>
        </div>
    )
}

const TableHeader = () => {
    const t = useTranslate()
    const {isLg} = useContext(LayoutContext)
    if (!isLg) return null
    return (
        <div
            className="grid px-3 py-2 background-100 border-b border-color-100 text-xs font-semibold text-color-300 uppercase tracking-wider gap-3"
            style={{gridTemplateColumns: FINANCE_ROW_GRID, width: 'max-content'}}>
            <span className="whitespace-nowrap">{t('student')}</span>
            <span className="whitespace-nowrap">{t('subjects')}</span>
            <span className="text-center whitespace-nowrap">{t('credits')}</span>
            <span className="whitespace-nowrap">{t('total payable')}</span>
            <span className="whitespace-nowrap">{t('waived')}</span>
            <span className="whitespace-nowrap">{t('amount paid')}</span>
            <span className="whitespace-nowrap">{t('remaining')}</span>
            <span className="whitespace-nowrap">{t('status')}</span>
            <span/>
        </div>
    )
}

// Khóa MIỄN PHÍ (fee_mode=free) - không có gì để quản lý học phí, "watermark, disable" cả khối theo
// yêu cầu: giữ NGUYÊN layout bên dưới (không mất công thiết kế lại) nhưng làm mờ + chặn thao tác
// (pointer-events-none - click/nhập thật sự KHÔNG có tác dụng, không chỉ mờ mắt), đè lên 1 watermark
// giải thích lý do + hướng dẫn đổi lại nếu cần.
const FreeModeWatermark = ({children}) => {
    const t = useTranslate()
    return (
        <div className="relative">
            <div className="pointer-events-none select-none opacity-40 grayscale">
                {children}
            </div>
            <div className="absolute inset-0 flex items-start justify-center pt-10 px-6">
                <div className="-rotate-3 px-5 py-3 rounded-xl bg-white border border-color-100 shadow-xl flex flex-col items-center gap-1 text-center max-w-sm">
                    <span className="font-black text-xl text-color-300 tracking-[0.2em] uppercase">
                        {t('free course watermark')}
                    </span>
                    <span className="text-xs text-color-400">
                        {t('free course finance disabled hint')}
                    </span>
                </div>
            </div>
        </div>
    )
}

// ─── Main Dashboard ───────────────────────────────────────────────────────────

export const CourseFinanceDashboard = ({course_id, can_edit = false, fee_mode, fee_amount, fee_currency_code, fee_structure_type}) => {
    const t = useTranslate()
    const location = useLocation()
    const isFreeCourse = fee_mode === course_fee_mode_options.free.value
    // FIXED (mặc định) - học phí LUÔN căn theo fee_amount cố định (CourseStudentFeeService::calculateTotal()),
    // course_fee_types không có tác dụng gì -> ẩn hẳn nút "Định mức phí" thay vì hiện 1 nút vô nghĩa
    // (BE cũng đã chặn create/editFeeType khi không phải itemized, xem CourseFinanceController).
    const isItemized = fee_structure_type === course_fee_structure_type_options.itemized.value

    const [viewingItem, setViewingItem] = useState(null)
    const [showFeeTypes, setShowFeeTypes] = useState(false)
    const [summary, setSummary] = useState(null)
    // Đổi giá trị này -> ép usePaginationHelper refetch lại cả trang (xem withKey/dependencies bên
    // dưới) - cần thiết vì list lấy nguyên object phẳng từ API, KHÔNG tự đồng bộ lại khi 1 hành động
    // rời rạc (sửa miễn giảm/rà soát tín chỉ ngay trên dòng, ghi nhận thu tiền...) đổi dữ liệu ở nơi
    // khác. bumpReload luôn gọi kèm fetchSummary (KPI cũng phải cập nhật theo).
    const [reloadToken, setReloadToken] = useState(0)
    const bumpReload = useCallback(() => {
        setReloadToken((v) => v + 1)
        fetchSummary({}, {id: course_id})
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [course_id])

    const onError = notifyOnError(t)

    const refresh_deps = useMemo(
        () => [course_id, `refreshToken=${_.get(location.state, 'refreshToken')}`],
        [course_id]
    )

    const {handleAsyncAction: fetchSummary, isLoading: loadingSummary} = useAsyncAction({
        apiInfo: course_getFinanceSummary_Api,
        onSuccess: (result, data) => setSummary(_.get(data, 'response.data', null)),
    })

    React.useEffect(() => {
        if (course_id) fetchSummary({}, {id: course_id})
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [course_id, ...refresh_deps])

    const {handleAsyncAction: recalcAll, isLoading: recalculatingAll} = useAsyncAction({
        apiInfo: course_recalculateCreditsForCourse_Api,
        onError,
        onSuccess: (result) => {
            bumpReload()
            message.success(t('recalculated students count', {count: result?.recalculated ?? 0}))
        },
    })

    const {handleAsyncAction: sendBulkReminder, isLoading: sendingBulk} = useAsyncAction({
        apiInfo: course_sendFeeReminderEmailBulk_Api,
        onError,
        onSuccess: (result) => {
            message.success(t('bulk reminder result', {
                sent: result?.sent ?? 0,
                skipped: result?.skipped ?? 0,
                failed: result?.failed ?? 0,
            }))
        },
    })

    const [pager_widget] = usePaginationHelper({
        pagerInfo: {
            RenderEntity: React.Fragment,
            PaginationMeta: NullAsDefault,
            apiInfo: course_getCourseStudentFinances_Api,
            query: bindQueryParams([{id: course_id}]),
            renderItem: (item, index) => (
                <StudentFinanceRow key={index} item={item} onView={setViewingItem} onChanged={bumpReload}/>
            ),
            renderWrap: (child, _list, count) => count > 0 && (
                <div className="border border-color-100 rounded-lg overflow-x-auto">
                    <TableHeader/>
                    <div className="flex flex-col">{child}</div>
                </div>
            ),
        },
        SortByWidget: NullAsDefault,
        renderSortBy: NullAsDefault,
        AdvancedFilter: FinanceFilterPanel,
        isToggleDefault: false,
        query_params: {
            keyword: 'keyword',
            payment_status: 'payment_status',
            has_waived: 'has_waived',
            has_paid: 'has_paid',
            has_remaining: 'has_remaining',
        },
        dependencies: [...refresh_deps, reloadToken],
        withKey: createPagerKey([...refresh_deps, reloadToken]),
    })

    const content = (
        <div className="flex flex-col gap-4">
            {/* Header - title/mô tả LUÔN đứng riêng 1 hàng, action buttons xuống hàng RIÊNG bên dưới
                và tự wrap theo đúng độ rộng màn hình (kể cả mobile) - trước đây 2 khối này chung 1
                hàng flex-row nên buttons bị ép vào 1 cột hẹp, xuống dòng lộn xộn. */}
            <div className="flex flex-col gap-3">
                <div className="flex flex-col">
                    <div className="flex items-center gap-2">
                        <PiCoinsFill size={20} className="text-amber-500"/>
                        <h2 className="font-bold text-xl text-gray-800 tracking-wide">{t('tuition fees')}</h2>
                    </div>
                    <div className="my-1 w-20 h-1 rounded-full bg-gray-400"/>
                    <p className="mt-1 text-sm text-color-300 tracking-wider">
                        {t('manage tuition fees description')}
                    </p>
                </div>
                {can_edit && (
                    <div className="flex flex-wrap gap-2">
                        <Button className="flex-shrink-0" icon={<PiArrowsClockwise size={14}/>} loading={recalculatingAll}
                                onClick={() => recalcAll({}, {id: course_id})}>
                            {t('recalculate all credits')}
                        </Button>
                        {/* Gửi hàng loạt - thao tác NHẠY CẢM (nội dung học phí gửi tới nhiều người cùng
                            lúc, gửi nhầm/gửi ẩu dễ phản cảm) nên PHẢI qua xác nhận, mặc định KHÔNG gửi
                            (autoFocusButton: 'cancel' - nút "Bỏ qua" được focus sẵn, bấm Enter không vô
                            tình gửi) - chỉ gửi thật khi bấm hẳn "Chắc chắn". Icon đổi từ mặc định
                            (thùng rác - showDeleteConfirmDialog vốn dùng cho xóa) sang phong bì để đúng
                            ngữ cảnh gửi email, không gây hiểu lầm là hành động xóa/hủy. */}
                        <Button className="flex-shrink-0" icon={<PiEnvelopeSimpleFill size={14}/>} loading={sendingBulk}
                                onClick={() => showDeleteConfirmDialog({
                                    translate: t,
                                    icon: <PiEnvelopeSimpleFill style={{color: '#d97706'}} className="anticon"/>,
                                    title: 'send bulk fee reminder',
                                    message: 'send fee reminder email to all unpaid students confirm',
                                    autoFocusButton: 'cancel',
                                    onOk: () => sendBulkReminder({}, {id: course_id}),
                                })}>
                            {t('send fee reminder email')}
                        </Button>
                        {isItemized && (
                            <Button className="flex-shrink-0" type="primary" icon={<PiGearFill size={14}/>} onClick={() => setShowFeeTypes(true)}>
                                {t('fee rate settings')}
                            </Button>
                        )}
                    </div>
                )}
            </div>

            {/* Khóa FIXED - không có biểu phí từng loại để khai, nhắc rõ học phí đang căn thẳng theo
                fee_amount cố định (sửa ở tab Cấu hình Khóa học), tránh admin đi tìm nút "Định mức
                phí" mà không thấy. */}
            {can_edit && !isItemized && (
                <p className="text-xs text-color-400 italic tracking-wide">
                    {t('fixed fee course hint')}
                </p>
            )}

            {/* KPI cards - LUÔN hiện thành 1 block (loading skeleton khi đang tải), theo yêu cầu. */}
            <FinanceSummaryBlock summary={summary} loading={loadingSummary}/>

            {/* Tóm tắt phí đã khai ở Cấu hình Khóa học - theo yêu cầu, đặt giữa KPI và bảng danh sách. */}
            <FeeConfigSummaryBlock
                course_id={course_id}
                fee_mode={fee_mode}
                fee_amount={fee_amount}
                fee_currency_code={fee_currency_code}
                fee_structure_type={fee_structure_type}
            />

            {pager_widget}

            {viewingItem && (
                <FullSideModalLayout
                    right={true}
                    title={
                        <div className="flex items-center gap-2">
                            <PiReceiptFill size={16} className="text-amber-500"/>
                            <span className="font-bold text-color-000 tracking-wide">{t('fee statement')}</span>
                        </div>
                    }
                    onCancel={() => setViewingItem(null)}>
                    <StudentFinanceDetail item={viewingItem} onChanged={bumpReload}/>
                </FullSideModalLayout>
            )}

            {showFeeTypes && (
                <FeeTypesPanel course_id={course_id} can_edit={can_edit} onCancel={() => {
                    setShowFeeTypes(false)
                    bumpReload()
                }}/>
            )}
        </div>
    )

    return isFreeCourse ? <FreeModeWatermark>{content}</FreeModeWatermark> : content
}

export default CourseFinanceDashboard
